import type { ReactNode } from 'react';
import { lazy, startTransition, Suspense, useEffect, useMemo, useState } from 'react';

import 'react-day-picker/style.css';
import { ApolloProvider } from '@apollo/client/react';
import { BricksProvider } from '@opendoor/bricks-next';
import { AddContextualData, addDatadogRUMGlobalContext } from '@opendoor/observability/slim';
import faviconPath from '@opendoor/shared-fe/faviconPath';
import '@js-joda/timezone/dist/js-joda-timezone-1970-2030';
import { AuthProvider } from 'platform/components/AuthContext';
import { LoaderProvider, SuspenseLoaderTracker } from 'platform/components/loader/LoaderContext';
import {
  ROOT_LOADER_APP_CONTENTS_ID,
  ROOT_LOADER_ID,
  RootLoader,
} from 'platform/components/loader/RootLoader';
import { RouteContentCommitSignal } from 'platform/components/loader/RouteContentCommitSignal';
import '@opendoor/primer';
import { CustomerBoundary } from 'platform/CustomerBoundary';
import 'core-js/es';

import 'index.css';
import 'intl';

import 'mapbox-gl/dist/mapbox-gl.css';
import { ErrorHandler } from 'platform/ErrorHandler';
import { useApptoolbox } from 'platform/hooks/useApptoolbox';
import { PageStalenessTimeProvider } from 'platform/hooks/useReloadStalePage';
import { useRootLoaderDone } from 'platform/hooks/useRootLoaderDone';
import { useLoadObservability, useObservability } from 'platform/observability';
import { ClientOnly } from 'platform/router/utils/ClientOnly';
import { TabVisibilityTrackerScript } from 'platform/TabVisibilityTrackerScript';
import { StyleSheet } from 'react-native';
import type { MetaFunction } from 'react-router';
import { Links, Meta, Outlet, Scripts, ScrollRestoration } from 'react-router';
import { apolloClient } from 'setupClient';
import { IS_DEV } from 'shared/helpers/env';
import { useTrackInitialLoad } from 'shared/hooks/useTrackPerformance';
import Tamagui from 'tamagui.config';

// Lazy component must be created at module scope; creating it inside the
// component body produces a fresh lazy wrapper (and a fresh suspending
// Promise) on every render, which traps route transitions in a suspense loop.
const DevTools =
  IS_DEV && typeof window !== 'undefined'
    ? lazy(async () => {
        const mod = await import('react-router-devtools/client');
        return { default: mod.withViteDevTools((): null => null) };
      })
    : null;

const APP_CONTEXTUAL_DATA = { framework: 'react-router' } as const;
const NO_SCRIPT_STYLE_HTML = {
  __html: `
        <style>
          #${ROOT_LOADER_ID} { display: none !important; }
          #cake-noscript { display: grid !important; }
          #${ROOT_LOADER_APP_CONTENTS_ID} { display: none !important; }
        </style>
        `,
} as const;
const NO_SCRIPT_CONTAINER_STYLE = {
  minHeight: '100vh',
  display: 'none',
  placeItems: 'center',
  padding: 32,
  boxSizing: 'border-box',
  fontFamily: 'Graphik, sans-serif',
  color: '#23201d',
  background: '#f9f8f8',
  textAlign: 'center',
} as const;
const NO_SCRIPT_MESSAGE_STYLE = {
  margin: 0,
  maxWidth: 420,
  fontSize: 16,
  lineHeight: 1.5,
} as const;

// Injects devtools when we're in dev and clientside.
// Local Suspense with a null fallback keeps a devtools chunk miss from swapping
// the whole app to LoadingPage via the outer Suspense boundary.
function LazyDevTools(): JSX.Element | null {
  if (!DevTools) {
    return null;
  }

  return (
    <ClientOnly>
      {() => (
        <Suspense fallback={null}>
          <DevTools />
        </Suspense>
      )}
    </ClientOnly>
  );
}

function AppContents({ children }: { children: ReactNode }) {
  useEffect(() => {
    // Wrapped in a startTransition to avoid blocking the main thread
    startTransition(() => {
      addDatadogRUMGlobalContext({ framework: 'react-router' });
    });
  }, []);
  useLoadObservability();
  useTrackInitialLoad();
  useApptoolbox();

  return (
    <AddContextualData data={APP_CONTEXTUAL_DATA}>
      <ApolloProvider client={apolloClient()}>
        <BricksProvider useObservability={useObservability} disableInjectCSS disableRootThemeClass>
          <Suspense fallback={<SuspenseLoaderTracker name="suspense:root" />}>
            <ClientOnly fallback={<SuspenseLoaderTracker name="suspense:client-only" />}>
              {() => <AuthProvider>{children}</AuthProvider>}
            </ClientOnly>
          </Suspense>
          <TabVisibilityTrackerScript />
          <LazyDevTools />
        </BricksProvider>
      </ApolloProvider>
    </AddContextualData>
  );
}

function AppContentsGate({ children }: { children: ReactNode }) {
  const rootLoaderDone = useRootLoaderDone();
  const [hasInitialRootLoaderFinished, setHasInitialRootLoaderFinished] = useState(rootLoaderDone);

  useEffect(() => {
    if (rootLoaderDone) {
      setHasInitialRootLoaderFinished(true);
    }
  }, [rootLoaderDone]);

  return (
    // eslint-disable-next-line @opendoor/no-html-elements -- wraps BricksProvider, so Box is not available yet
    <div
      id={ROOT_LOADER_APP_CONTENTS_ID}
      aria-hidden={hasInitialRootLoaderFinished ? undefined : true}
      {...(hasInitialRootLoaderFinished ? {} : { inert: '' })}
    >
      {children}
    </div>
  );
}

/* eslint-disable @opendoor/no-html-elements -- no-JS fallback renders outside BricksProvider */
function NoScriptFallback() {
  return (
    <>
      <noscript dangerouslySetInnerHTML={NO_SCRIPT_STYLE_HTML} />
      <div id="cake-noscript" style={NO_SCRIPT_CONTAINER_STYLE}>
        <p style={NO_SCRIPT_MESSAGE_STYLE}>
          JavaScript is required to load this page. Please enable JavaScript and refresh.
        </p>
      </div>
    </>
  );
}
/* eslint-enable @opendoor/no-html-elements */

export const meta: MetaFunction = () => [{ title: 'Opendoor' }];

// Rendered on both prerender and client, so React owns the full document and
// route-level <Meta />/<Links /> updates reach the DOM.
export function Layout({ children }: { children: ReactNode }) {
  const tamaguiCSS = Tamagui.getCSS();
  const reactNativeStylesheetHTML = useMemo(
    () => ({
      // @ts-expect-error -- React Native Web provides textContent at runtime, but its sheet type omits it.
      __html: StyleSheet.getSheet().textContent,
    }),
    [],
  );
  const tamaguiStylesheetHTML = useMemo(() => ({ __html: tamaguiCSS }), [tamaguiCSS]);

  return (
    <html lang="en">
      <head>
        <Meta />
        <meta name="framework" content="react-router" />
        <meta charSet="utf-8" />
        <link rel="shortcut icon" href={faviconPath({ railsEnv: import.meta.env.ODFE_ENV })} />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <meta name="theme-color" content="#FAF9F6" />
        <meta name="msapplication-navbutton-color" content="#FAF9F6" />
        <meta name="apple-mobile-web-app-status-bar-style" content="#FAF9F6" />
        <Links />
        {/* Preload fonts to aid with CLS and LCP. URLs must match the @font-face
          src in index.css exactly so the browser reuses the preload. */}
        <link
          href="https://cdn.opendoor.com/fonts/Graphik-Bold-Web.woff2"
          rel="preload"
          as="font"
          type="font/woff2"
          crossOrigin="anonymous"
        />
        <link
          href="https://cdn.opendoor.com/fonts/Graphik-Semibold-Web.woff2"
          rel="preload"
          as="font"
          type="font/woff2"
          crossOrigin="anonymous"
        />
        <link
          href="https://cdn.opendoor.com/fonts/Graphik-Medium-Web.woff2"
          rel="preload"
          as="font"
          type="font/woff2"
          crossOrigin="anonymous"
        />
        <link
          href="https://cdn.opendoor.com/fonts/Graphik-Regular-Web.woff2"
          rel="preload"
          as="font"
          type="font/woff2"
          crossOrigin="anonymous"
        />
        <style
          id="react-native-stylesheet"
          suppressHydrationWarning
          dangerouslySetInnerHTML={reactNativeStylesheetHTML}
        />
        <style suppressHydrationWarning dangerouslySetInnerHTML={tamaguiStylesheetHTML} />
      </head>
      <body id="root">
        <LoaderProvider>
          <RootLoader />
          <NoScriptFallback />
          <AppContentsGate>
            <AppContents>{children}</AppContents>
          </AppContentsGate>
        </LoaderProvider>
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

export default function App() {
  return (
    <CustomerBoundary>
      <PageStalenessTimeProvider>
        <Outlet />
        <RouteContentCommitSignal />
      </PageStalenessTimeProvider>
    </CustomerBoundary>
  );
}

/**
 * Catches errors anywhere in its child component tree and renders a fallback component instead of the component that crashed.
 */
export function ErrorBoundary() {
  return (
    <>
      <RouteContentCommitSignal />
      <ErrorHandler />
    </>
  );
}

export function HydrateFallback() {
  return null;
}
